// tslint:disable:max-line-length
export type STRING_MAP<P> = Map<string, P>;
export type ENUM_MAP<E, P> = Map<E, P>;

export function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
    return o.reduce((res, key) => {
        res[key] = key;
        return res;
    }, Object.create(null));
}

// http://2ality.com/2015/08/es6-map-json.html
export function mapToJson(map): string {
    return JSON.stringify([...map]);
}

export function jsonToMap<K, V>(jsonStr): Map<K, V> {
    return new Map<K, V>(JSON.parse(jsonStr));
}

export type ARRAY_STR_ARRAY = [string[]];
/**
 * JSON Schema Type enumerations
 * https://developers.google.com/discovery/v1/type-format
 * http://json-schema.org/latest/json-schema-validation.html#rfc.section.3.2
 * @type {{[K in string | string | string | string | string | string | string]:K}}
 */
export const JSON_TYPE = strEnum(["any", "array", "boolean", "integer", "number", "object", "string"]);
export type JSON_TYPE = keyof typeof JSON_TYPE;

/**
 * JSON Schema Type Format enumerations
 * https://developers.google.com/discovery/v1/type-format
 * http://json-schema.org/latest/json-schema-validation.html#rfc.section.3.2
 * @type {{[K in string | string | string | string | string | string | string | string | string | string | string | string | string | string]:K}}
 */
export const JSON_FORMAT = strEnum([
    "int32",
    "uint32",
    "double",
    "float",
    "byte",
    "date",
    "date_time",
    "int64",
    "uint64",
    "object",
    "email",
    "hostname",
    "ipv4",
    "ipv6",
    "uri",
    "uri_reference",
    "uri_template",
    "json_pointer"]);
export type JSON_FORMAT = keyof typeof JSON_FORMAT;

/**
 * GO Lang Type enumerations
 * @type {{[K in string | string | string | string | string | string | string | string]:K}}
 */
export const GOLANG_TYPE = strEnum(["string", "integer", "number", "boolean", "object", "array", "any", "complex_object"]);
export type GOLANG_TYPE = keyof typeof GOLANG_TYPE;

/**
 * JSON Schema Type Information
 */
export interface JSONSCHEMATypeInfo {
    type: JSON_TYPE;
    format?: JSON_FORMAT;
    pattern?: string;
    min?: number;
    max?: number;
}

export type JSON_SCHEMA_TYPE = JSONSCHEMATypeInfo;

const JSON_RT_TYPE_MAPPING: ENUM_MAP<GOLANG_TYPE, JSON_SCHEMA_TYPE> = new Map<GOLANG_TYPE, JSON_SCHEMA_TYPE>();
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.string] = { type: JSON_TYPE.string };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.integer] = { type: JSON_TYPE.integer };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.number] = { type: JSON_TYPE.number };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.boolean] = { type: JSON_TYPE.boolean };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.object] = { type: JSON_TYPE.object };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.array] = { type: JSON_TYPE.array };
// JSON_RT_TYPE_MAPPING[GOLANG_TYPE.any] = { type: JSON_TYPE.string };
JSON_RT_TYPE_MAPPING[GOLANG_TYPE.complex_object] = { type: JSON_TYPE.object };

/**
 * Returns a {{JSON_SCHEMA_TYPE}}
 * @param {GOLANG_TYPE} rtType
 * @return {JSON_SCHEMA_TYPE}
 * @constructor
 */
export function RTtoJSONTypeMap(rtType: GOLANG_TYPE): JSON_SCHEMA_TYPE {
    return JSON_RT_TYPE_MAPPING[rtType];
}

/**
 * https://stackoverflow.com/questions/14425568/interface-type-check-with-typescript
 * interface A {
 *  propOfA: string;
 *  methodOfA: Function;
 * }
 *
 * let objectA: any = { propOfA: '' };
 *
 * // Check if objectA partially implements A
 * let implementsA = implementsTKeys<A>(objectA, ['propOfA']);
 *
 * console.log(implementsA); // true
 *
 * objectA.methodOfA = () => true;
 *
 * // Check if objectA fully implements A
 * implementsA = implementsTKeys<A>(objectA, ['propOfA', 'methodOfA']);
 *
 * console.log(implementsA); // true
 *
 * objectA = {};
 *
 * // Check again if objectA fully implements A
 * implementsA = implementsTKeys<A>(objectA, ['propOfA', 'methodOfA']);
 *
 * console.log(implementsA); // false, as objectA now is an empty object
 * @param obj
 * @param keys
 */
export function implementsTKeys<T>(obj: any, keys: (keyof T)[]): obj is T {
    if (!obj || !Array.isArray(keys)) {
        return false;
    }
    try {
        const implementKeys = keys.reduce((impl, key) => impl && key in obj, true);
        return implementKeys;
    } catch (e) {
        return false;
    }
}

/**
 * Messaging handler function prototype
 */
export type HANDLER_FUNCTION<T> = (data: T) => void;

export type integer = number;

export const HTTP_METHOD = strEnum(["get", "post", "put", "delete"]);
export type HTTP_METHOD = keyof typeof HTTP_METHOD;

export const HTTP_COOKIES = strEnum(["ScribeSession", "AtmosphereSession", "tsc", "domain", "AtmosphereWIFlogoSession"]);
export type HTTP_COOKIES = keyof typeof HTTP_COOKIES;

export const HTTP_HEADERS = strEnum(["Authorization", "jwt", "Origin", "jwtToken"]);
export type HTTP_HEADERS = keyof typeof HTTP_HEADERS;

export const ES_TYPES = strEnum(["number", "object", "string", "boolean"]);
export type ES_TYPES = keyof typeof ES_TYPES;

// REFERENCE: https://en.wikipedia.org/wiki/List_of_HTTP_status_codes
// HTTP Status Code Mapping
export const HTTP_STATUS_CODES = {
    CONTINUE: 100,
    SWITCHING_PROTOCOLS: 101,
    PROCESSING: 102,
    EARLY_HINTS: 103,
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NON_AUTHORATIVE_INFO: 203,
    NO_CONTENT: 204,
    RESET_CONTENT: 205,
    PARTIAL_CONTENT: 206,
    MULTI_STATUS: 207,
    ALREADY_REPORTED: 208,
    IM_USED: 226,
    MULTIPLE_CHOICES: 300,
    MOVED_PERMANENTLY: 301,
    FOUND: 302,
    SEE_OTHER: 303,
    NOT_MODIFIED: 304,
    USE_PROXY: 305,
    SWITCH_PROXY: 306,
    TEMPORARY_REDIRECT: 307,
    PERMANENT_REDIRECT: 308,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    PAYMENT_REQUIRED: 402,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    NOT_ACCEPTABLE: 406,
    PROXY_AUTH_REQUIRED: 407,
    REQUEST_TIMEOUT: 408,
    CONFLICT: 409,
    GONE: 410,
    LENGTH_REQUIRED: 411,
    PRECONDITION_FAILED: 412,
    PAYLOAD_TOO_LARGE: 413,
    URI_TOO_LONG: 414,
    UNSUPPORTED_MEDIA_TYPE: 415,
    RANGE_NOT_SATISFIABLE: 416,
    EXPECTATION_FAILED: 417,
    IM_A_TEAPOT: 418,
    PAGE_EXPIRED: 419,
    MISDIRECTED_REQUEST: 421,
    UNPROCESSABLE_ENTITY: 422,
    LOCKED: 423,
    FAILED_DEPENDENCY: 424,
    UPGRADE_REQUIRED: 426,
    PRECONDITION_REQUIRED: 428,
    TOO_MANY_REQUESTS: 429,
    REQUEST_HEADER_FIELDS_TOO_LARGE: 431,
    INTERNAL_SERVER_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
    GATEWAY_TIMEOUT: 504
};
Object.keys(HTTP_STATUS_CODES).reduce((obj, key) => {
    obj[HTTP_STATUS_CODES[key]] = key;
    return obj;
}, HTTP_STATUS_CODES);

// JWT audience claims
export const JWT_AUD_TYPES = strEnum(["BPM"]);
export type JWT_AUD_TYPES = keyof typeof JWT_AUD_TYPES;
